import { DEFAULT_TEXT } from '../constants/strings';
import { AccessibleCompany, Company } from '../types';

export const jsonFormatter = (value: object) => {
    const space = 2;
    return JSON.stringify(value, null, space);
};

export const relationFormatter = <T>(data: Record<number, T>, field: string) => (value: string | number) =>
    data[value] ? data[value][field] : DEFAULT_TEXT;

export const dateFormatter = (dateString: string) => {
    if (!dateString) {
        return DEFAULT_TEXT;
    }

    return new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).format(
        new Date(dateString)
    );
};

export const defaultFormatter = (cellValue: any) => {
    return cellValue || DEFAULT_TEXT;
};

export const numericFormatter = (cellValue: any) => {
    return cellValue || 0;
};

export const companyFormatter = (getCompanyNameFunc: any) => (value: number) => {
    let name = getCompanyNameFunc(value);
    if (!name) {
        name = DEFAULT_TEXT;
    }
    return name;
};

export const authTypeLookup = (getAuthTypeFunc: any) => (value: number) => {
    return getAuthTypeFunc(value);
};

export const parseDateToString = (d: Date) => {
    const valueWithoutZero = 10;
    const month = d.getMonth() + 1;
    const day = d.getDate();

    return `${d.getFullYear()}-${month < valueWithoutZero ? '0' : ''}${month}-${
        day < valueWithoutZero ? '0' : ''
    }${day}`;
};

export const numberFormatter = (number: number, minimumFractionDigits = 0) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits }).format(number);


/**
 * phoneFormatter formats phone numbers for display.
 * - North American format (10 digits OR 11 digits starting with +1): formats as (XXX) XXX-XXXX
 * - International format (11+ digits): formats as +XXXXXXXXXXXX (E.164 format)
 * - Invalid formats: returns original input (useful for showing invalid input as-is)
 */
export const phoneFormatter = (inputPhoneNumber: string): string => {
    const sanitizedNumber = sanitizePhoneNumber(inputPhoneNumber);

    return sanitizedNumber ? sanitizedNumber : inputPhoneNumber;
};

/**
 * Sanitizes a phone number by removing all non-digit characters.
 * - North American format (10 digits OR 11 digits starting with +1): formats as (XXX) XXX-XXXX
 * - International format (11+ digits): formats as +XXXXXXXXXXXX (E.164 format)
 * - Invalid phone number: returns empty string.
 */
export const sanitizePhoneNumber = (phone: string): string => {
    const cleaned = ('' + phone).replace(/\D/g, '');

    // Numbers with 10 digits or 11 digits starting with 1
    const tenDigitMatch = cleaned.match(/^1?(\d{3})(\d{3})(\d{4})$/);
    if (tenDigitMatch) {
        // tslint:disable-next-line:no-magic-numbers
        return ['(', tenDigitMatch[1], ') ', tenDigitMatch[2], '-', tenDigitMatch[3]].join('');
    }

    // Other international numbers with 11 or more digits
    const moreDigitsMatch = cleaned.match(/^(\+)?(\d{11,})$/);
    if (moreDigitsMatch) {
        return  "+" + moreDigitsMatch[2];
    }

    return "";
}

export const moneyFormatter = (value: number, digits: number = 0) => {
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: digits
    });

    return formatter.format(value);
};

export const checkFloatValue = (value: string | number) =>
    value
        .toString()
        .replace(/,/g, '')
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const stripTags = (value: string) => value.replace(/(<([^>]+)>)/gi, '');

export const addressFromCompanyFormatter = ({
    address1,
    address2,
    city,
    state,
    zipcode
}: Company | AccessibleCompany) => `${address1}${address2 ? ' ' + address2 : ''}, ${city}, ${state} ${zipcode}`;
